import {
  parentOf,
  subcategoriesOf,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import type { PickedSubcategory } from "@/domains/freelance/taxonomy/types";
import { isJobCategory } from "@/domains/job-offer/create/constants";
import type { ProfileTaxonomyProposal } from "../api";
import type { SectorValue } from "../context/OnboardingContext";

/** Subcategories kept for each sector (freelancer-categories FE10). */
const MAX_SUBCATEGORIES_PER_SECTOR = 3;

const emptySector = (): SectorValue => ({
  job_category: null,
  job_category_secondary: null,
  job_subcategories: [],
});

/**
 * The `sector` step value a CV or LinkedIn analysis proposes (AC17).
 *
 * The backend proposal is read defensively: a category is kept only if it is
 * one of the 8 (`isJobCategory`), the secondary only if it differs from the
 * primary and the primary exists, and the subcategories become
 * `{ key, origin: "suggested" }`, deduplicated, at most 3 per sector in the
 * analysis order. A subcategory whose parent is not one of the kept sectors is
 * dropped.
 *
 * "The tree is not loaded" is detected from the tree itself, not from
 * `taxonomyStatus()` (it stays `idle` when the tree is set directly): the tree
 * is loaded when at least one kept sector has children in it. Then a key with
 * an unknown parent is dropped too. Without the tree no key can be placed under
 * a sector, so all the deduplicated keys are kept and the backend decides.
 *
 * Reads the taxonomy store, so call it where a tracked read is harmless (an
 * event handler or a `produce`).
 */
export function sectorFromProposal(
  proposal: ProfileTaxonomyProposal | null | undefined,
): SectorValue {
  if (!proposal) return emptySector();

  const primary = isJobCategory(proposal.job_category)
    ? proposal.job_category
    : null;
  if (!primary) return emptySector();

  const secondary =
    isJobCategory(proposal.job_category_secondary) &&
    proposal.job_category_secondary !== primary
      ? proposal.job_category_secondary
      : null;

  const keys = Array.isArray(proposal.job_subcategories)
    ? [...new Set(proposal.job_subcategories)].filter(
        (key) => typeof key === "string" && key.length > 0,
      )
    : [];

  const kept: string[] = secondary ? [primary, secondary] : [primary];
  const treeLoaded = kept.some((sector) => subcategoriesOf(sector).length > 0);

  const job_subcategories: PickedSubcategory[] = [];
  if (treeLoaded) {
    const perSector = new Map<string, number>();
    for (const key of keys) {
      const parent = parentOf(key);
      if (!parent || !kept.includes(parent)) continue;
      const count = perSector.get(parent) ?? 0;
      if (count >= MAX_SUBCATEGORIES_PER_SECTOR) continue;
      perSector.set(parent, count + 1);
      job_subcategories.push({ key, origin: "suggested" });
    }
  } else {
    for (const key of keys) {
      job_subcategories.push({ key, origin: "suggested" });
    }
  }

  return {
    job_category: primary,
    job_category_secondary: secondary,
    job_subcategories,
  };
}
